// KÖZÖS saját-RSVP hook: a `PUT /meetings/:id/rsvp` self-scope mutáció + optimista helyi státusz.
//
// MIÉRT KÖZÖS: KÉT felület állítja a saját részvételi választ — a naptár RÉSZLET-KÁRTYÁJA
// (`MeetingParticipantsPanel`) és az ÁTTEKINTÉS „Aktuális megbeszélések" widgetje (a legacy dashboard inline
// „Ott leszek / Nem megyek / Talán" gombjai). Ugyanaz a végpont, ugyanazok a toast-üzenetek és ugyanaz a
// cache-invalidálás — a második előfordulásnál a repo DRY-szabálya a közös rétegbe rendeli.
//
// Az OPTIMISTA státusz szándékosan LOKÁLIS: a szerver-válasz után a lista-invalidálás a forrás-igazság, de a
// felhasználónak azonnali visszajelzés kell (a kártya nem várhat a refetchre).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { meetingKeys } from '@/features/meetings/api';
import { api } from '@/lib/api';
import { useEntityMutation } from '@/lib/use-entity-mutation';

/** A saját RSVP két állítható értéke — a `pending` (kezdő) állapotra visszatérni nem lehet. */
export type RsvpChoice = 'confirmed' | 'declined';

export interface MeetingRsvpControls {
  /** A megjelenítendő SAJÁT státusz: az optimista helyi érték, ha már választottam; egyébként a szerverről jött. */
  myStatus: string | null;
  /** Fut-e épp a mentés (a gombok tiltásához). */
  isPending: boolean;
  setRsvp: (status: RsvpChoice) => void;
}

/**
 * @param meetingId a megbeszélés id-je (a self-scope végpont path-paramja)
 * @param serverStatus a szerverről jött SAJÁT státusz (`null`, ha nem vagyok résztvevő)
 */
export function useMeetingRsvp(
  meetingId: string,
  serverStatus: string | null,
): MeetingRsvpControls {
  const { t } = useTranslation();
  const [optimistic, setOptimistic] = useState<string | null>(null);

  const rsvp = useEntityMutation<RsvpChoice>({
    mutationFn: (status) =>
      api.meetingRsvp.mutation({ params: { id: meetingId }, body: { status } }),
    invalidateKeys: [meetingKeys.lists()],
    messages: {
      success: t('toast:meetings.rsvpSaved'),
      conflict: t('toast:meetings.conflict'),
      forbidden: t('toast:meetings.forbidden'),
      validation: t('toast:meetings.validation'),
      error: t('toast:meetings.error'),
    },
  });

  return {
    myStatus: optimistic ?? serverStatus,
    isPending: rsvp.isPending,
    setRsvp: (status) => {
      setOptimistic(status);
      rsvp.mutate(status);
    },
  };
}
